// hooks/useSessionCleanup.js - Session cleanup logic and handlers
// Extracted from PlatformSelector.js and Dashboard.js

import { useCallback } from 'react';
import logger from '../services/logger';
import { supabase } from '../lib/supabase';
import {
  stopSpotifyWebPlayer,
  stopHtmlAudioElements,
  stopYouTubePlayback,
  clearMusicTimers,
  clearPlatformSessions,
  clearAllStorage,
  cleanupWebAudioContexts,
  resetUIElements,
  cleanupMusicEventListeners,
  emergencyCleanup,
  performComprehensiveCleanup
} from '../services/sessionCleanupHelpers.js';

/**
 * Custom hook for session cleanup operations
 */
export const useSessionCleanup = () => {
  
  /**
   * Handle guest session expiry (from PlatformSelector.js)
   */
  const handleGuestSessionExpired = useCallback(async (navigate) => {
    logger.info('⏰ Guest session expired - starting comprehensive cleanup...');
    
    try {
      // Step 1: Stop any playing music immediately
      logger.debug('🎵 Stopping music playback...');
      
      await stopSpotifyWebPlayer();
      stopHtmlAudioElements();
      stopYouTubePlayback();
      
      logger.success('Music playback stopped');
      
      // Step 2: Clear all platform sessions for security
      logger.debug('🛡️ Clearing platform sessions...');
      await clearPlatformSessions();
      
      // Step 3: Clear all local storage and session data
      logger.debug('🧹 Clearing local storage...');
      clearAllStorage();
      
      // Step 4: Show user-friendly message
      logger.debug('📢 Showing session expired message...');
      
      const userConfirmed = window.confirm(
        'Your 15-minute guest session has expired.\n\n' +
        'Click OK to return to the login page and create a free account for unlimited access.'
      );
      
      // Step 5: Force redirect regardless of user action
      logger.info('🚪 Redirecting to auth page...');
      window.location.replace('/');
      
    } catch (error) {
      logger.error('Error during session cleanup:', error);
      
      // Emergency cleanup - force everything
      emergencyCleanup();
      window.location.replace('/');
    }
  }, []);

  /**
   * Handle session expiry from Dashboard (enhanced version)
   */
  const handleDashboardSessionExpired = useCallback(async (navigate, setUIState) => {
    logger.info('⏰ Guest session expired in dashboard - starting comprehensive cleanup...');
    
    try {
      // Step 1: Stop music player immediately
      logger.debug('🎵 Stopping dashboard music playback...');
      
      // Access player context if available
      if (typeof setUIState === 'function') {
        try {
          setUIState(prev => ({
            ...prev,
            isPlaying: false,
            currentTrack: null
          }));
        } catch (err) {
          logger.warn('Could not update player state:', err);
        }
      }
      
      // Use comprehensive cleanup
      await performComprehensiveCleanup();
      
      // Step 2: Clear all platform sessions
      logger.debug('🛡️ Clearing all platform sessions...');
      await clearPlatformSessions();
      
      // Step 3: Clear all storage
      logger.debug('🧹 Clearing all storage...');
      clearAllStorage();
      
      // Step 4: Show clear message and redirect
      logger.debug('📢 Showing session expired message...');
      
      alert(
        'Your 15-minute guest session has expired.\n\n' +
        'You will now be redirected to create a free account for unlimited access.'
      );
      
      // Step 5: Force immediate redirect
      logger.info('🚪 Forcing redirect to auth page...');
      window.location.href = '/';
      
      // Backup redirect
      setTimeout(() => {
        window.location.replace('/');
      }, 100);
      
    } catch (error) {
      logger.error('Error during dashboard session cleanup:', error);
      
      // Emergency fallback
      emergencyCleanup();
      window.location.href = '/';
    }
  }, []);

  /**
   * Enhanced MongoDB logout with comprehensive cleanup
   */
  const createEnhancedMongoLogout = useCallback((user, platformStates, navigate) => {
    return async () => {
      try {
        logger.info('🔄 Starting MongoDB logout with comprehensive cleanup...');
        
        // Step 1: Stop all music playback immediately
        logger.debug('🎵 Stopping all music playback...');
        await performComprehensiveCleanup();
        
        // Step 2: Save safe user data to database (if authenticated user)
        if (user) {
          logger.debug('💾 Saving user preferences...');
          
          const safeData = {
            lastActivePlatform: platformStates.spotify && platformStates.spotify.active ? 'spotify' : 
                               (platformStates.youtube && platformStates.youtube.active ? 'youtube' : null),
            dashboardPreferences: {
              sidebarOpen: true,
            },
            uiPreferences: {
              theme: 'dark',
            }
          };
          
          try {
            const saveResponse = await fetch('/api/user/save-preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                'x-auth-token': localStorage.getItem('authToken')
              },
              body: JSON.stringify(safeData)
            });
            
            if (saveResponse.ok) {
              logger.success('User preferences saved to database');
            } else {
              logger.warn('⚠️ Could not save preferences, continuing with logout');
            }
          } catch (err) {
            logger.warn('⚠️ Preference save error:', err);
          }
        }
        
        // Step 3: Clear all platform browser sessions + Supabase tokens
        logger.debug('🛡️ Clearing platform sessions + Supabase tokens...');
        let supabaseToken = null;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          supabaseToken = session?.access_token || null;
        } catch (e) {
          logger.warn('Could not get Supabase session for logout:', e);
        }
        await clearPlatformSessions(supabaseToken);
        
        // Step 4: Clear all local storage (MongoDB logout)
        logger.debug('🧹 Clearing local storage...');
        clearAllStorage();
        
        // Step 5: Clean up any remaining audio context or players
        logger.debug('🎵 Final audio cleanup...');
        await cleanupWebAudioContexts();
        
        logger.success('Final cleanup completed');
        
        // Step 6: Redirect to auth page
        logger.info('🚪 Redirecting to auth page');
        window.location.href = '/';
        
      } catch (error) {
        logger.error('Error during MongoDB logout:', error);
        
        // Emergency cleanup and redirect
        emergencyCleanup();
        window.location.href = '/';
      }
    };
  }, []);

  /**
   * Default cleanup handler for platforms page
   */
  const performDefaultCleanup = useCallback(async () => {
    try {
      logger.debug('🎵 Performing default cleanup...');
      await performComprehensiveCleanup();
      logger.success('Default cleanup completed');
    } catch (error) {
      logger.error('Error during default cleanup:', error);
      emergencyCleanup();
    }
  }, []);

  /**
   * Enhanced back to platforms handler with comprehensive cleanup (from Dashboard)
   */
  const handleBackToPlatformsWithCleanup = useCallback(async (navigate, setUIState) => {
    try {
      logger.info('🏠 Enhanced Back to Platforms clicked - starting comprehensive music cleanup...');
      
      // Step 1: Stop music player immediately with advanced cleanup
      logger.debug('🎵 Stopping dashboard music with advanced cleanup...');
      
      // Update player context to stop playback with enhanced state clearing
      if (setUIState) {
        setUIState(prev => ({
          ...prev,
          isPlaying: false,
          currentTrack: null,
          playlist: { ...prev.playlist, tracks: [], currentIndex: 0 },
          platform: prev.platform, // Keep platform but stop playback
          progress: 0,
          duration: 0,
          volume: prev.volume // Preserve volume setting
        }));
      }
      
      // Use comprehensive cleanup
      await performComprehensiveCleanup();
      
      // Step 2: Clear comprehensive music-related timers and intervals
      logger.debug('⏰ Clearing all music-related timers and intervals...');
      clearMusicTimers();
      
      // Step 3: Clean up Web Audio API contexts
      logger.debug('🔊 Cleaning up Web Audio API contexts...');
      await cleanupWebAudioContexts();
      
      // Step 4: Reset UI elements and visual states
      logger.debug('🎨 Resetting UI visual states...');
      resetUIElements();
      
      // Step 5: Clean up any music-related event listeners
      logger.debug('👂 Cleaning up music event listeners...');
      cleanupMusicEventListeners();
      
      // Step 6: Wait for all cleanup operations to complete
      logger.debug('⏳ Waiting for cleanup completion...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Step 7: Final verification and navigation
      logger.debug('🔍 Final verification of cleanup...');
      
      // Verify no audio is playing
      const stillPlayingAudio = document.querySelectorAll('audio:not([paused]), video:not([paused])');
      if (stillPlayingAudio.length > 0) {
        logger.warn('⚠️ Some audio still playing, forcing stop...');
        stillPlayingAudio.forEach(element => {
          element.pause();
          element.currentTime = 0;
        });
      }
      
      // Step 8: Navigate to platforms
      logger.info('🚪 Navigating to platforms page...');
      navigate('/platforms');
      
      logger.success('Comprehensive cleanup completed');
      
    } catch (error) {
      logger.error('❌ Error during enhanced back to platforms cleanup:', error);
      
      // Emergency comprehensive cleanup
      emergencyCleanup();
      
      // Navigate anyway
      navigate('/platforms');
    }
  }, []);

  /**
   * Force session cleanup for emergency situations
   */
  const forceSessionCleanup = useCallback(async () => {
    logger.warn('🚨 Force session cleanup initiated...');
    
    try {
      await performComprehensiveCleanup();
      clearAllStorage();
      await clearPlatformSessions();
    } catch (error) {
      logger.error('Error during force cleanup:', error);
      emergencyCleanup();
    }
  }, []);

  return {
    handleGuestSessionExpired,
    handleDashboardSessionExpired,
    createEnhancedMongoLogout,
    performDefaultCleanup,
    handleBackToPlatformsWithCleanup,
    forceSessionCleanup,
    // Direct access to utility functions
    stopSpotifyWebPlayer,
    stopHtmlAudioElements,
    stopYouTubePlayback,
    clearMusicTimers,
    clearPlatformSessions,
    clearAllStorage,
    performComprehensiveCleanup
  };
};